import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Selector from '../../components/forms/Selector';
import IconSave from '../../components/icons/IconSave';
import IconDelete from '../../components/icons/IconDelete';
import IconAdd from '../../components/icons/IconAdd';
import language from '../../variables/language';
import '../../css/modals.css';

const url = '/api/student/plannedevents';

const HOURS = Array.from({length:24}, (_, i) => String(i).padStart(2,'0'));
// 5-minute steps keep the dropdown short enough to fit without scrolling.
const MINUTES = Array.from({length:12}, (_, i) => String(i * 5).padStart(2,'0'));
// outside 09:00-20:00 is flagged red, matching the "before 9 / after 20" rule
const hourColorClass = (h) => {
   const n = Number(h);
   return (n < 9 || n > 20) ? 'time-red' : 'time-green';
}
// both dropdowns use the same cap so they always pick the same open direction
const TIME_LIST_MAX_HEIGHT = 200;

// a native <select>'s popup can't be restyled and picks its own open
// direction per element (which is why the hour list, being taller, could
// flip open upward while the shorter minute list stayed down) - this is a
// small custom dropdown instead, so both always match and options can carry
// their own colors. The list is rendered via a portal straight onto <body>:
// .modal-window has backdrop-filter set, which (like transform/filter) makes
// it a new containing block for position:fixed descendants, so a plain fixed
// div here would actually be positioned relative to the modal, not the
// viewport - a portal sidesteps that entirely.
function TimeOptionSelect({ value, onChange, options, colorClass, placeholder }) {
   const [open, setOpen] = useState(false);
   const [coords, setCoords] = useState(null);
   const wrapRef = useRef(null);
   const listRef = useRef(null);

   useEffect(() => {
      if(!open) return;
      const handleOutside = (e) => {
         const inTrigger = wrapRef.current && wrapRef.current.contains(e.target);
         const inList = listRef.current && listRef.current.contains(e.target);
         if(!inTrigger && !inList) setOpen(false);
      };
      const handleReflow = (e) => {
         // scrolling *inside* the option list itself (to reach hour 22 etc.)
         // must not close it - only close when something outside moves.
         if(listRef.current && e.target && listRef.current.contains(e.target)) return;
         setOpen(false);
      };
      document.addEventListener('mousedown', handleOutside);
      window.addEventListener('scroll', handleReflow, true);
      window.addEventListener('resize', handleReflow);
      return () => {
         document.removeEventListener('mousedown', handleOutside);
         window.removeEventListener('scroll', handleReflow, true);
         window.removeEventListener('resize', handleReflow);
      };
   }, [open]);

   const handleToggle = () => {
      if(!open && wrapRef.current) {
         const rect = wrapRef.current.getBoundingClientRect();
         const spaceBelow = window.innerHeight - rect.bottom;
         const openUp = spaceBelow < TIME_LIST_MAX_HEIGHT && rect.top > spaceBelow;
         setCoords({
            left: rect.left,
            width: rect.width,
            top: openUp ? undefined : rect.bottom + 4,
            bottom: openUp ? (window.innerHeight - rect.top + 4) : undefined,
         });
      }
      setOpen(o => !o);
   }

   const handlePick = (opt) => {
      onChange(opt);
      setOpen(false);
   }

   return (
      <div className='planned-time-select' ref={wrapRef}>
         <div className={'planned-time-select-trigger' + (open ? ' open' : '')} onClick={handleToggle}>
            <span className={'planned-time-select-value' + (value ? (colorClass ? ' ' + colorClass(value) : '') : ' planned-time-select-placeholder')}>
               {value || placeholder}
            </span>
         </div>
         {open && coords ? createPortal(
            <div className='planned-time-select-list' ref={listRef} style={{...coords, maxHeight: TIME_LIST_MAX_HEIGHT}}>
               {options.map(opt => (
                  <div key={opt}
                     className={'planned-time-select-option' + (opt === value ? ' selected' : '') + (colorClass ? ' ' + colorClass(opt) : '')}
                     onClick={() => handlePick(opt)}>
                     {opt}
                  </div>
               ))}
            </div>,
            document.body
         ) : ''}
      </div>
   );
}

const resolveValue = (val) => {
   if(val && typeof val === 'object' && val.id !== undefined) {
      return String(val.id);
   }
   if(val === null || val === undefined || val === 'null' || val === 'undefined') {
      return '';
   }
   return String(val);
}

const emptyValues = () => ({planedStudentEventType:'', isActive:'', dateTime:'', message:''});

export default function PlannedEventDetailModal({ isOpen, eventId, onClose, onSaved, onDeleted }) {
   const navigate = useNavigate();
   const [values, setValues] = useState(emptyValues());
   const [initialValues, setInitialValues] = useState(emptyValues());
   const [isCycled, setIsCycled] = useState(0);
   const [loading, setLoading] = useState(false);
   const [saving, setSaving] = useState(false);
   const [weekdays, setWeekdays] = useState([]);
   const [newWeekDay, setNewWeekDay] = useState('');
   const [newHour, setNewHour] = useState('');
   const [newMinute, setNewMinute] = useState('');
   const [addingDay, setAddingDay] = useState(false);

   const loadEvent = () => {
      setLoading(true);
      axios.get(url + '/' + eventId, { withCredentials: true })
         .then(response => {
            const data = response.data || {};
            const flat = {
               planedStudentEventType: resolveValue(data.planedStudentEventType),
               isActive: data.isActive === 1 ? '1' : (data.isActive === 2 ? '2' : ''),
               dateTime: resolveValue(data.dateTime),
               message: resolveValue(data.message),
            };
            setValues(flat);
            setInitialValues(flat);
            setIsCycled(data.isCycled);
         })
         .catch(err => { if(err.response && err.response.status === 401) navigate('/login'); })
         .finally(() => setLoading(false));
   }
   const loadWeekdays = () => {
      axios.get(url + '/weekdays/' + eventId, { withCredentials: true })
         .then(response => setWeekdays(Array.isArray(response.data) ? response.data : []))
         .catch(() => setWeekdays([]));
   }

   useEffect(() => {
      if(!isOpen || !eventId) {
         return;
      }
      loadEvent();
      loadWeekdays();
      setNewWeekDay('');
      setNewHour('');
      setNewMinute('');
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, eventId]);

   const isDirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(initialValues), [values, initialValues]);

   if(!isOpen) return '';

   const lang = language[language.lang];
   const plang = lang.plannedEvents;
   const stopProp = e => e.stopPropagation();

   const handleFieldChange = (name, value) => {
      setValues(prev => ({...prev, [name]: value}));
   }
   const handleDiscard = () => setValues(initialValues);

   const handleSave = () => {
      if(!isDirty || saving) return;
      setSaving(true);
      const payload = {};
      Object.keys(values).forEach(key => {
         if(key === 'dateTime' && isCycled === 1) return;
         if(values[key] !== '') payload[key] = values[key];
      });
      axios.put(url + '/' + eventId, JSON.stringify(payload), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         loadEvent();
         onSaved();
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setSaving(false));
   }

   const handleDeleteEvent = () => {
      if(!window.confirm(plang.deleteConfirm)) return;
      axios.delete(url + '/' + eventId, { withCredentials: true })
         .then(() => onDeleted())
         .catch(err => console.log(err));
   }

   const handleAddWeekday = () => {
      if(!newWeekDay || newHour === '' || newMinute === '' || addingDay) return;
      setAddingDay(true);
      const time = newHour + ':' + newMinute;
      axios.post(url + '/weekdays', JSON.stringify({ weekDay: newWeekDay, time, planedStudentEvent: String(eventId) }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setNewWeekDay('');
         setNewHour('');
         setNewMinute('');
         loadWeekdays();
      }).catch(err => console.log(err))
        .finally(() => setAddingDay(false));
   }
   const handleRemoveWeekday = (dayId) => {
      axios.delete(url + '/weekdays/' + dayId, { withCredentials: true })
         .then(() => loadWeekdays())
         .catch(err => console.log(err));
   }

   const sortedWeekdays = weekdays.slice().sort((a,b) => a.weekDay - b.weekDay || (a.time > b.time ? 1 : -1));

   return (
      <div className="modal-menu" onClick={onClose} style={{ display: 'flex' }}>
         <div className="modal-window lesson-detail-window" onClick={stopProp}>
            <div className='lesson-detail-header'>
               <h3 className="modal-w-title lesson-detail-title">{lang['Config planed event']}</h3>
               <div className='lesson-detail-delete-btn' onClick={handleDeleteEvent}>
                  <IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} />
               </div>
            </div>

            {loading ? (
               <div className='event-edit-loading'>{lang['Loading ...']}</div>
            ) : (
               <div className='lesson-detail-body'>
                  <div className='lesson-detail-form-col'>
                     <div className='student-card-section'>
                        <div className='student-card-fields-grid m-block'>
                           <div className='flex-from-input-wraper student-lesson-create-field-full' data-label={lang.planedStudentEventType}>
                              <Selector className='submit-field form-selection txt-c'
                                 name='planedStudentEventType' url='/api/student/plannedevents/types'
                                 value={values.planedStudentEventType} disabled={false}
                                 selectorsHundler={(value) => handleFieldChange('planedStudentEventType', value)} />
                           </div>
                        </div>
                        <div className='student-card-fields-grid m-block'>
                           <div className='flex-from-input-wraper student-card-field' data-label={lang.isActive}>
                              <Selector className='submit-field form-selection txt-c'
                                 name='isActive' url='/api/booleans' value={values.isActive} disabled={false}
                                 selectorsHundler={(value) => handleFieldChange('isActive', value)} />
                           </div>
                           {isCycled === 1 ? (
                              <div className='flex-from-input-wraper student-card-field planned-readonly-field m-block' data-label={lang.isCycled}>
                                 <span className='student-lessons-badge on'>
                                    <i className='student-lessons-badge-dot'></i>{lang.Yes}
                                 </span>
                              </div>
                           ) : (
                              <div className='flex-from-input-wraper student-card-field m-block' data-label={plang.when}>
                                 <input className='submit-field from-input txt-c' type='datetime-local'
                                    value={values.dateTime && values.dateTime.length >= 16 ? values.dateTime.slice(0,16) : values.dateTime}
                                    onChange={(e) => handleFieldChange('dateTime', e.target.value)} />
                              </div>
                           )}
                        </div>
                        <div className='student-card-fields-grid'>
                           <div className='flex-from-input-wraper student-lesson-create-field-full' data-label={lang.message}>
                              <textarea className='submit-field from-input txt-c event-edit-textarea'
                                 placeholder={lang.message}
                                 value={values.message}
                                 onChange={(e) => handleFieldChange('message', e.target.value)} />
                           </div>
                        </div>
                     </div>

                     {isDirty ? (
                        <div className='student-card-form-footer'>
                           <span className='student-card-unsaved-hint'>{lang.unsavedChanges}</span>
                           <div className='student-card-discard-link' onClick={handleDiscard}>{lang.cancel}</div>
                           <button type='button' className='student-card-save-btn' disabled={saving} onClick={handleSave}>
                              <IconSave />
                              {saving ? lang['Loading ...'] : lang.save}
                           </button>
                        </div>
                     ) : ''}
                  </div>

                  {isCycled === 1 ? (
                     <div className='lesson-detail-participants-col'>
                        <p className='student-card-section-title'>{lang.weekDays}</p>
                        <div className='lesson-detail-participants-list'>
                           <div className='planned-schedule-chips'>
                              {sortedWeekdays.map(d => (
                                 <span className='student-lessons-badge planned-weekday-chip' key={d.id}>
                                    {lang.weekDaysShort[d.weekDay - 1]} {String(d.time).slice(0,5)}
                                    <span className='planned-weekday-chip-remove' onClick={() => handleRemoveWeekday(d.id)}>×</span>
                                 </span>
                              ))}
                              {sortedWeekdays.length === 0 ? <p className='lesson-detail-empty'>{plang.noSchedule}</p> : ''}
                           </div>
                        </div>
                        <p className='student-card-section-title'>{lang['Add day and time']}</p>
                        <div className='lesson-detail-add-row'>
                           <div className='planned-add-field'>
                              <span className='planned-add-field-label'>{lang.weekDay}</span>
                              <Selector className='submit-field form-selection txt-c'
                                 name='weekDay' url='/api/weekdays' value={newWeekDay} disabled={false}
                                 selectorsHundler={(value) => setNewWeekDay(value)} />
                           </div>
                           <div className='planned-add-field'>
                              <span className='planned-add-field-label'>{plang.hour}</span>
                              <TimeOptionSelect value={newHour} onChange={setNewHour} options={HOURS}
                                 colorClass={hourColorClass} placeholder='--' />
                           </div>
                           <span className='planned-time-picker-sep'>:</span>
                           <div className='planned-add-field'>
                              <span className='planned-add-field-label'>{plang.minute}</span>
                              <TimeOptionSelect value={newMinute} onChange={setNewMinute} options={MINUTES}
                                 placeholder='--' />
                           </div>
                           <div className='student-lessons-add-btn' onClick={handleAddWeekday} title={lang['Add day and time']}>
                              <IconAdd />
                           </div>
                        </div>
                     </div>
                  ) : ''}
               </div>
            )}
         </div>
      </div>
   );
}
